import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ArrowRightLeft,
  Calendar,
  CheckSquare,
  ChevronDown,
  Crosshair,
  Database,
  ExternalLink,
  FileText,
  Folder,
  FolderKanban,
  FolderOpen,
  GitBranch,
  Kanban,
  Layers,
  Library,
  Search,
  Server,
  Spline,
  X,
} from 'lucide-react';
import CompactSelectPicker from '../ui/CompactSelectPicker';
import Tooltip from '../ui/Tooltip';
import OsScroll from '../ui/OsScroll';
import { TYPE_STYLE, isDarkTheme, typeColor } from './graphTheme';
import { neighbors } from '../../graph/graphQueries';
import { ScopeTag } from './GraphScopePicker';
import { canWriteScope, projectAccepts } from '../../graph/projectsStore';

export const TYPE_ICON = Object.freeze({
  project: FolderKanban,
  collection: Folder,
  folder: FolderOpen,
  request: ArrowRightLeft,
  environment: Layers,
  board: Kanban,
  task: CheckSquare,
  doc_folder: Folder,
  document: FileText,
  doc_workspace: Library,
  appointment: Calendar,
  db_connection: Database,
  ssh_host: Server,
  git_repo: GitBranch,
});

/** Tinted rounded badge with the type's icon — the panel's visual anchor. */
export function TypeBadge({ type, size = 'md' }) {
  const Icon = TYPE_ICON[type] || Folder;
  const color = typeColor(type, isDarkTheme());
  const px = size === 'sm' ? 12 : 16;
  return (
    <span className={`graph-type-badge graph-type-badge--${size}`} style={{ color, background: `${color}1f` }} aria-hidden>
      <Icon size={px} />
    </span>
  );
}

/** "Payments API › Refunds": the containers above a node, nearest last. */
export function parentPath(index, nodeId) {
  const path = [];
  let current = nodeId;
  for (let guard = 0; guard < 6; guard += 1) {
    // A per-iteration constant: `current` is reassigned below, and a callback
    // should not close over a variable the loop it lives in rewrites.
    const at = current;
    const up = (index.adj.get(at) || []).find(
      ({ edge }) => edge.rel === 'contains' && edge.to === at && edge.origin === 'derived'
    );
    if (!up) break;
    const parent = index.byId.get(up.edge.from);
    if (!parent) break;
    path.unshift(parent.label);
    current = parent.id;
  }
  return path;
}

/**
 * The details panel (owner, 13.09.2026: "use your design abilities to make
 * that side toggle better looking and overall better UX").
 *
 * Structure, top to bottom:
 *   header   type badge · type · title · where it lives · close
 *   actions  Open (primary) · Center · Draw a link
 *   projects the projects it is in (removable chips) + an "Add to project" picker
 *   links    filter (All / Linked by you / Automatic) + search when long,
 *            collapsible groups by relation, rows with type icon, variable
 *            anchor, hover actions (center, unlink)
 */
export default function GraphDetails({
  node,
  index,
  projects,
  onClose,
  onPick,
  onOpen,
  onCenter,
  onStartLink,
  onAddToProject,
  onRemoveFromProject,
  onUnlink,
  onHoverNode,
  onRowContextMenu,
}) {
  const { t } = useTranslation();
  const [origin, setOrigin] = useState('all');
  const [q, setQ] = useState('');
  const [collapsed, setCollapsed] = useState(() => new Set());
  const dark = isDarkTheme();

  const around = useMemo(() => neighbors(index, node.id, { depth: 1, cap: 500 }), [index, node.id]);
  const path = useMemo(() => parentPath(index, node.id), [index, node.id]);

  const links = useMemo(() => {
    const out = [];
    for (const e of around?.edges || []) {
      if (e.from !== node.id && e.to !== node.id) continue;
      const other = index.byId.get(e.from === node.id ? e.to : e.from);
      if (!other) continue;
      // An item's project memberships have their own section above; a
      // project's own members ARE its connections, so keep those.
      if (node.type !== 'project' && e.rel === 'contains' && e.from.startsWith('project:')) continue;
      out.push({ edge: e, other, outgoing: e.from === node.id });
    }
    return out;
  }, [around, index, node.id, node.type]);

  const counts = useMemo(
    () => ({
      all: links.length,
      explicit: links.filter((l) => l.edge.origin === 'explicit').length,
      derived: links.filter((l) => l.edge.origin !== 'explicit').length,
    }),
    [links]
  );

  const groups = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const map = new Map();
    for (const l of links) {
      if (origin === 'explicit' && l.edge.origin !== 'explicit') continue;
      if (origin === 'derived' && l.edge.origin === 'explicit') continue;
      if (needle && !l.other.label.toLowerCase().includes(needle)) continue;
      const key = `${l.edge.rel}.${l.outgoing ? 'out' : 'in'}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(l);
    }
    for (const list of map.values()) list.sort((a, b) => a.other.label.localeCompare(b.other.label));
    return [...map.entries()].sort((a, b) => b[1].length - a[1].length);
  }, [links, origin, q]);

  const memberOf = useMemo(
    () =>
      new Set(
        (index.adj.get(node.id) || [])
          .filter(({ edge }) => edge.rel === 'contains' && edge.to === node.id && edge.from.startsWith('project:'))
          .map(({ edge }) => edge.from.slice('project:'.length))
      ),
    [index, node.id]
  );
  const inProjects = projects.filter((p) => memberOf.has(p.id));
  // Only projects this item may join: a team project takes its own team's
  // items, and nothing from a viewer (projectsStore refuses the same writes).
  const addable = projects.filter((p) => !memberOf.has(p.id) && projectAccepts(p, node));

  const toggleGroup = (key) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const meta = [node.method, node.engine].filter(Boolean);

  return (
    <aside className="graph-details" data-testid="graph-details" aria-label={node.label}>
      <header className="gd-head">
        <TypeBadge type={node.type} />
        <div className="gd-head-text">
          <div className="gd-eyebrow">
            {t(TYPE_STYLE[node.type]?.labelKey || 'graph.type.item')}
            {meta.map((m) => (
              <span key={m} className="gd-pill">
                {m}
              </span>
            ))}
          </div>
          <h3 className="gd-title" title={node.label}>
            {node.label}
          </h3>
          <ScopeTag node={node} className="gd-scope" />
          {path.length > 0 && (
            <div className="gd-path" title={path.join(' › ')}>
              {t('graph.details.inPath', { path: path.join(' › ') })}
            </div>
          )}
        </div>
        <button type="button" className="graph-icon-btn gd-close" onClick={onClose} aria-label={t('common.close')}>
          <X size={14} />
        </button>
      </header>

      <div className="gd-actions">
        <button type="button" className="gd-btn gd-btn--primary" onClick={onOpen}>
          <ExternalLink size={13} /> {t('graph.open')}
        </button>
        <Tooltip content={t('graph.center')} side="bottom">
          <button type="button" className="gd-btn gd-btn--icon" onClick={onCenter} aria-label={t('graph.center')}>
            <Crosshair size={14} />
          </button>
        </Tooltip>
        <Tooltip content={t('graph.details.drawLinkHint')} side="bottom">
          <button type="button" className="gd-btn" onClick={onStartLink}>
            <Spline size={13} /> {t('graph.details.drawLink')}
          </button>
        </Tooltip>
      </div>

      {/* The app's scrollbar (OverlayScrollbars), not the native one (owner). */}
      <OsScroll className="gd-scroll">
        {node.type !== 'project' && (
          <section className="gd-section">
            <div className="gd-section-head">
              <span>{t('graph.projects')}</span>
            </div>
            <div className="gd-chips">
              {inProjects.length === 0 && <span className="gd-muted">{t('graph.details.notInProject')}</span>}
              {inProjects.map((p) => (
                <span key={p.id} className="gd-chip">
                  <span className="graph-dot" style={{ background: p.color }} />
                  <span className="gd-chip-label">{p.name}</span>
                  {canWriteScope(p.scopeKey) && (
                    <button
                      type="button"
                      className="gd-chip-x"
                      onClick={() => onRemoveFromProject(p.id)}
                      aria-label={t('graph.details.removeFromProject', { name: p.name })}
                      title={t('graph.details.removeFromProject', { name: p.name })}
                    >
                      <X size={11} />
                    </button>
                  )}
                </span>
              ))}
            </div>
            {addable.length > 0 && (
              <CompactSelectPicker
                className="gd-add-project graph-picker"
                menuClassName="graph-picker-menu"
                value=""
                placeholder={t('graph.details.addToProject')}
                onChange={(id) => id && onAddToProject(id)}
                options={addable.map((p) => ({
                  value: p.id,
                  label: p.name,
                  leading: <span className="graph-dot" style={{ background: p.color }} />,
                }))}
                searchable={addable.length > 8}
                aria-label={t('graph.details.addToProject')}
              />
            )}
          </section>
        )}

        <section className="gd-section gd-section--links">
          <div className="gd-section-head">
            <span>{t('graph.details.connections')}</span>
            <span className="gd-count">{counts.all}</span>
          </div>

          {counts.all > 0 && (
            <div className="gd-segment" role="group" aria-label={t('graph.details.connections')}>
              {[
                ['all', t('graph.details.filterAll'), counts.all],
                ['explicit', t('graph.details.filterExplicit'), counts.explicit],
                ['derived', t('graph.details.filterDerived'), counts.derived],
              ].map(([key, label, n]) => (
                <button
                  key={key}
                  type="button"
                  aria-pressed={origin === key}
                  className={`gd-segment-btn${origin === key ? ' gd-segment-btn--on' : ''}`}
                  onClick={() => setOrigin(key)}
                  disabled={n === 0 && key !== 'all'}
                >
                  {label}
                  <span className="gd-segment-n">{n}</span>
                </button>
              ))}
            </div>
          )}

          {counts.all > 8 && (
            <label className="gd-search">
              <Search size={12} aria-hidden />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('graph.details.filterLinks')} aria-label={t('graph.details.filterLinks')} />
              {q && (
                <button type="button" className="graph-icon-btn" onClick={() => setQ('')} aria-label={t('common.close')}>
                  <X size={11} />
                </button>
              )}
            </label>
          )}

          {counts.all === 0 && (
            <div className="gd-empty">
              <Spline size={18} aria-hidden />
              <p>{t('graph.details.noLinks')}</p>
              <button type="button" className="gd-btn" onClick={onStartLink}>
                {t('graph.details.drawLink')}
              </button>
            </div>
          )}
          {counts.all > 0 && groups.length === 0 && <div className="gd-muted gd-pad">{t('graph.details.noMatch')}</div>}

          {groups.map(([key, items]) => {
            const isCollapsed = collapsed.has(key);
            return (
              <div key={key} className="gd-group">
                <button type="button" className="gd-group-head" onClick={() => toggleGroup(key)} aria-expanded={!isCollapsed}>
                  <ChevronDown size={12} className={`gd-chevron${isCollapsed ? ' gd-chevron--closed' : ''}`} aria-hidden />
                  <span>{t(`graph.rel.${key}`)}</span>
                  <span className="gd-count">{items.length}</span>
                </button>
                {!isCollapsed && (
                  <ul className="gd-rows">
                    {items.map(({ edge, other }) => {
                      const Icon = TYPE_ICON[other.type] || Folder;
                      return (
                        <li
                          key={edge.id}
                          className="gd-row"
                          onMouseEnter={() => onHoverNode?.(other.id)}
                          onMouseLeave={() => onHoverNode?.(null)}
                          onFocus={() => onHoverNode?.(other.id)}
                          onBlur={() => onHoverNode?.(null)}
                          // Same right-click menu as the item on the canvas (owner).
                          onContextMenu={(e) => {
                            if (!onRowContextMenu) return;
                            e.preventDefault();
                            onRowContextMenu({ node: other, clientX: e.clientX, clientY: e.clientY });
                          }}
                        >
                          <button type="button" className="gd-row-main" onClick={() => onPick(other.id)} title={other.label}>
                            <Icon size={13} style={{ color: typeColor(other.type, dark) }} aria-hidden />
                            <span className="gd-row-label">{other.label}</span>
                            {edge.anchor?.variable && <code className="gd-anchor">{`{{${edge.anchor.variable}}}`}</code>}
                            {edge.origin === 'explicit' && <span className="gd-dot-explicit" title={t('graph.details.filterExplicit')} />}
                          </button>
                          <span className="gd-row-actions">
                            <button type="button" className="graph-icon-btn" onClick={() => onCenter(other.id)} title={t('graph.center')} aria-label={t('graph.center')}>
                              <Crosshair size={12} />
                            </button>
                            {edge.origin === 'explicit' && canWriteScope(edge.scope) && (
                              <button type="button" className="graph-icon-btn" onClick={() => onUnlink(edge)} title={t('graph.removeLink')} aria-label={t('graph.removeLink')}>
                                <X size={12} />
                              </button>
                            )}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            );
          })}
          {around?.truncated && <div className="gd-muted gd-pad">{t('graph.truncated')}</div>}
        </section>
      </OsScroll>

      <footer className="gd-foot">{t('graph.details.tip')}</footer>
    </aside>
  );
}
